import * as React from 'react'
import { DriverAddForm, DriverFromContextType } from '../types/driver'
import { useQueryCountries } from '@/modules/shared/query-client/useQueryCountries'
import { GroupModel } from '../types/groups'
import { useLoadingStore } from '@/modules/shared/stores/loadingStore'
import useGroupService from '../services/GroupService'
import useDriverService from '../services/DriverService'

const DriverFormContext = React.createContext<DriverFromContextType | null>(
  null,
)
export const DriverFormProvider = ({
  children,
  companyId,
  driverId,
  isEdit,
}: {
  children: React.ReactNode
  companyId: string
  driverId?: number | null
  isEdit?: boolean
}) => {
  const { getGroups } = useGroupService()
  const { getDriver } = useDriverService()
  const startLoading = useLoadingStore((s) => s.startLoading)
  const [companyIdState, setCompanyIdState] = React.useState<string>(companyId)
  const [formType, setFormType] = React.useState<
    'simple' | 'full-basic' | 'full-additional'
  >('simple')
  const [isEditState] = React.useState<boolean>(!!isEdit)
  const [isActive, setIsActive] = React.useState<boolean>(true)
  const [data, setData] = React.useState<DriverAddForm | null>(null)
  const [isReadyToSubmit, setIsReadyToSubmit] = React.useState(false)
  const [groups, setGroups] = React.useState<GroupModel[]>([])
  const [isOpenGroupsModal, setIsOpenAddDriverModal] = React.useState(false)

  const isFullForm = formType !== 'simple'

  const { data: countries } = useQueryCountries({
    companyId: companyId,
  })

  const countriesOptions = React.useMemo(() => {
    return Array.isArray(countries)
      ? countries.map((c) => ({
          label: c.name,
          value: c.id.toString(),
        }))
      : []
  }, [countries])

  const fetchDriver = async () => {
    const stopLoading = startLoading()
    try {
      const data = await getDriver({
        companyId,
        driverId,
        withPersonalData: true,
      })
      setIsActive(data?.isActive)
      setData((prev) => ({ ...prev, ...data }))
    } catch (err) {
      throw err
    } finally {
      stopLoading()
    }
  }
  const fetchGroups = async () => {
    const stopLoading = startLoading()
    try {
      const res = await getGroups({
        companyId,
      })
      setGroups(res)
    } catch (err) {
      console.error('Error fetching groups:', err)
    } finally {
      stopLoading()
    }
  }

  const refreshGroups = React.useCallback(async () => {
    await fetchGroups()
  }, [companyId])

  const selectedGroups = React.useMemo(() => {
    if (!data?.groups || !Array.isArray(data.groups)) return []

    return groups.filter((group) => data.groups.includes(group.id))
  }, [groups, data?.groups])

  React.useEffect(() => {
    if (isEdit && driverId) {
      fetchDriver()
    }
  }, [isEdit])

  React.useEffect(() => {
    fetchGroups()
  }, [companyId, isEditState])

  const value = React.useMemo(
    () => ({
      isReadyToSubmit,
      setIsReadyToSubmit,
      groups,
      countriesOptions,
      setData,
      data,
      isFullForm,
      formType,
      setFormType,
      isActive,
      setIsActive,
      selectedGroups,
      countries,
      setIsOpenAddDriverModal,
      isOpenGroupsModal,
      refreshGroups,
      isEditState,
      companyIdState,
      setCompanyIdState,
    }),
    [
      isReadyToSubmit,
      isEditState,
      selectedGroups,
      formType,
      isActive,
      data,
      countriesOptions,
      isFullForm,
      isOpenGroupsModal,
      groups,
      refreshGroups,
      companyIdState,
    ],
  )

  return (
    <DriverFormContext.Provider value={value}>
      {children}
    </DriverFormContext.Provider>
  )
}

export const useDriverForm = () => {
  const ctx = React.useContext(DriverFormContext)
  if (!ctx)
    throw new Error('useDriverForm must be used within DriverFormProvider')
  return ctx
}
